import React, { useState, useEffect, useRef, useMemo } from "react";
import "../styles/Home.scss";
import { LuAlarmClock } from "react-icons/lu";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaRegStar,
  FaVestPatches,
} from "react-icons/fa";
import { TbKayak } from "react-icons/tb";
import { MdOutlineBookmarkAdd } from "react-icons/md";
import { RiCameraAiLine } from "react-icons/ri";
import { useLanguage } from "../context/LanguageContext";
import usePublicPricing from "../hooks/usePublicPricing";
import { PRICE_CARD_CONFIG } from "../constants/equipmentPricing";
import { getPriceLabel } from "../utils/priceLabel";

const HOW_STEPS_CONFIG = [
  {
    key: "home.how.steps.0",
    number: 1,
    title: "Pick your ride",
    text: "Choose SUP or kayak, solo or double, add any goodies (dry bag, phone case, kid vest).",
    icon: TbKayak,
  },
  {
    key: "home.how.steps.1",
    number: 2,
    title: "Set the clock",
    text: "Pick date, start time, and how long you want to splash.",
    icon: LuAlarmClock,
  },
  {
    key: "home.how.steps.2",
    number: 3,
    title: "Book & boom!",
    text: "Checkout online. You’ll get a confirmation with an email and all the juicy details.",
    icon: MdOutlineBookmarkAdd,
  },
  {
    key: "home.how.steps.5",
    number: 6,
    title: "Cruise back & high-five",
    text: "Return on time, we rinse the gear, you keep the grin. Rebook if you’re hooked.",
    icon: FaRegStar,
  },
  {
    key: "home.how.steps.4",
    number: 5,
    title: "Snack, snap, splash",
    text: "Pause for photos, float breaks, and that “I live here now” feeling.",
    icon: RiCameraAiLine,
  },
  {
    key: "home.how.steps.3",
    number: 4,
    title: "Show up & suit up",
    text: "Arrive 10 min early with an ID. We fit your vest, hand you the paddle, and stash your stuff.",
    icon: FaVestPatches,
  },
];

const WHY_TAGS_CONFIG = [
  { key: "home.why.tags.0", text: "Friendly local team", variant: "dark", },
  { key: "home.why.tags.1", text: "No deposit needed", variant: "light", },
  { key: "home.why.tags.2", text: "Clean, safe, well-maintained equipment", variant: "light", },
  { key: "home.why.tags.3", text: "Comfy conditions", variant: "dark", },
  { key: "home.why.tags.4", text: "Passion for water adventures", variant: "light", },
  { key: "home.why.tags.5", text: "Flexible booking – reschedule if needed", variant: "dark",},
  { key: "home.why.tags.6", text: "Sunset options", variant: "light", },
  { key: "home.why.tags.7", text: "Located right by the beach", variant: "dark", },
];

const FAQ_CONFIG = [
  
  {
    questionKey: "home.faq.items.1.question",
    answerKey: "home.faq.items.1.answer",
    question: "How do I book?",
    answer:
      "All online. Go to booking page, decide on type and choose location → time → pay → get QR code. Quick and clean.",
  },
  {
    questionKey: "home.faq.items.2.question",
    answerKey: "home.faq.items.2.answer",
    question: "What shall I bring with me?",
    answer:
      "Please bring a valid ID and qr code from your booking confirmation.",
  },
  {
    questionKey: "home.faq.items.3.question",
    answerKey: "home.faq.items.3.answer",
    question: "Do I need experience?",
    answer:
      "Nope! If you can swim and balance a bit — you’re ready.",
  },
  {
    questionKey: "home.faq.items.4.question",
    answerKey: "home.faq.items.4.answer",
    question: "Do I need to know how to swim?",
    answer:
      "Basic swimming ability is required for safety. Life vests are provided and required.",
  },
  {
    questionKey: "home.faq.items.5.question",
    answerKey: "home.faq.items.5.answer",
    question: "Can I cancel or change my booking?",
    answer:
      "Yes! Go to your profile → “My bookings”. If the time slot is free, you can move it.",
  },
  {
    questionKey: "home.faq.items.6.question",
    answerKey: "home.faq.items.6.answer",
    question: "How does pick-up work?",
    answer:
      "Come to the partner location → Show your QR code → Staff enters it in the system → You get your gear → paddle away",
  },
  {
    questionKey: "home.faq.items.7.question",
    answerKey: "home.faq.items.7.answer",
    question: "Do you provide life jackets?",
    answer:
      "Of course!",
  },
  {
    questionKey: "home.faq.items.8.question",
    answerKey: "home.faq.items.8.answer",
    question: "Do you take a deposit?",
    answer:
      "Nope, we trust you to return the gear",
  },
  {
    questionKey: "home.faq.items.9.question",
    answerKey: "home.faq.items.9.answer",
    question: "What if weather gets bad?",
    answer:
      "If it’s unsafe (storm/wind), please contact us we will cancel the bookings and generate a voucher for you for you next bookins ",
  },
  {
    questionKey: "home.faq.items.10.question",
    answerKey: "home.faq.items.10.answer",
    question: "What if I return equipment late?",
    answer:
      "No drama — the system will add 15 min. If very late → small late fee",
  },
  {
    questionKey: "home.faq.items.11.question",
    answerKey: "home.faq.items.11.answer",
    question: "Is there a weight limit?",
    answer:
      "Most boards support up to ~120 kg. Kayaks vary but generally accommodate up to ~150 kg",
  },
  {
    questionKey: "home.faq.items.12.question",
    answerKey: "home.faq.items.12.answer",
    question: "What if something breaks?",
    answer:
      "Small scratches = totally fine. If something big breaks or gets lost → repair fee.",
  },
  {
    questionKey: "home.faq.items.13.question",
    answerKey: "home.faq.items.13.answer",
    question: "Do you provide storage for valuables?",
    answer:
      "We can hold small items (keys/phone) in addition waterproof bag for on-board storage in included.",
  },
  {
    questionKey: "home.faq.items.14.question",
    answerKey: "home.faq.items.14.answer",
    question: "How do I contact you?",
    answer:
      "info@wayli.eu or through instagram page @wayli.eu. We usually respond within a few hours during the day.",
  },
  {
    questionKey: "home.faq.items.15.question",
    answerKey: "home.faq.items.15.answer",
    question: "Any rules about alcohol?",
    answer:
      "For safety, no alcohol before or during your session. Save the toast for after 🥤.",
  },
];

const getPerView = (width) => {
  if (width >= 1200) return 3;
  if (width >= 768) return 2;
  return 1;
};

const getHowColumns = (width) => {
  if (width <= 520) return 1;
  if (width <= 820) return 2;
  return 3;
};

const buildWavyPath = (points, amplitude = 28) => {
  if (!points || points.length === 0) return "";
  if (points.length === 1) {
    const { x, y } = points[0];
    return `M ${x} ${y}`;
  }

  const segments = [`M ${points[0].x} ${points[0].y}`];

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const len = Math.max(Math.hypot(dx, dy), 1);
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;
    const waves = Math.max(2, Math.round(len / 120));

    for (let w = 1; w <= waves; w += 1) {
      const startT = (w - 1) / waves;
      const endT = w / waves;
      const midT = (startT + endT) / 2;
      const endX = prev.x + dx * endT;
      const endY = prev.y + dy * endT;
      const midX = prev.x + dx * midT;
      const midY = prev.y + dy * midT;
      const alt = w % 2 === 0 ? -1 : 1;
      const waveAmp = Math.min(amplitude, len / 2.5) * alt;

      const ctrlX = midX + nx * waveAmp;
      const ctrlY = midY + ny * waveAmp;

      segments.push(`Q ${ctrlX} ${ctrlY} ${endX} ${endY}`);
    }
  }

  return segments.join(" ");
};

const isTouchEnvironment = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
    return true;
  }
  if ("ontouchstart" in window) return true;
  return typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
};

const HomePage = () => {
  const { t } = useLanguage();
  const { pricingRowsByKey, pricingRowsById } = usePublicPricing();
  const initialPerView =
    typeof window !== "undefined" ? getPerView(window.innerWidth) : 3;
  const [perView, setPerView] = useState(initialPerView);
  const [isTouchDevice, setIsTouchDevice] = useState(() => isTouchEnvironment());
  const [howColumns, setHowColumns] = useState(() =>
    typeof window !== "undefined" ? getHowColumns(window.innerWidth) : 3
  );
  const [howPath, setHowPath] = useState({ groups: [], width: 0, height: 0 });

  const cards = useMemo(
    () =>
      PRICE_CARD_CONFIG.map((card) => ({
        ...card,
        title: card.title,
        priceLabel: "Free",
        items: card.items,
      })),
    []
  );

  const howCards = useMemo(
    () =>
      HOW_STEPS_CONFIG.map((step) => ({
        ...step,
        title: t(`${step.key}.title`, step.title),
        text: t(`${step.key}.text`, step.text),
      })),
    [t]
  );

  const orderedHowCards = useMemo(() => {
    if (howColumns >= 3) return howCards;
    const order = [1, 2, 3, 4, 5, 6];
    const mapByNumber = howCards.reduce((acc, card) => {
      acc[card.number] = card;
      return acc;
    }, {});
    return order.map((num) => mapByNumber[num]).filter(Boolean);
  }, [howCards, howColumns]);

  const whyTags = useMemo(
    () =>
      WHY_TAGS_CONFIG.map((tag) => ({
        ...tag,
        text: t(tag.key, tag.text),
      })),
    [t]
  );

  const faqs = useMemo(
    () =>
      FAQ_CONFIG.map((item) => ({
        question: t(item.questionKey, item.question),
        answer: t(item.answerKey, item.answer),
      })),
    [t]
  );

  const heroHeadingLine1 = t("home.hero.headingLine1", "Ride the Waves.");
  const heroHeadingLine2 = t(
    "home.hero.headingLine2",
    "Your Water Adventure"
  );
  const heroHeadingLine3 = t("home.hero.headingLine3", "Starts Here.");
  const heroDescription = t(
    "home.hero.description",
    "Rent SUP boards, kayaks, and gear in minutes — no hassle. Available daily. Perfect for beginners and pros."
  );
  const heroCta = t("home.hero.cta", "Book Now");
  const navigate = useNavigate();
  const priceTitle = t("home.price.title", "Price");
  
  const howTitle = t("home.how.title", "How it works");
  const whyTitle = t("home.why.title", "Why Paddle With Us?");
  const faqTitle = t("home.faq.title", "FAQ");
  const faqToggleLabel = t("home.faq.toggleLabel", "Toggle FAQ");

  const [faqVisible, setFaqVisible] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(null);

  const step = useMemo(() => 100 / perView + 0.4, [perView]);

  const [startIndex, setStartIndex] = useState(0);
  const [offset, setOffset] = useState(() => -(100 / initialPerView + 0.4));
  const [animating, setAnimating] = useState(false);
  const [noTransition, setNoTransition] = useState(true);
  const [direction, setDirection] = useState(null);
  const [flippedCardIndex, setFlippedCardIndex] = useState(null);

  const [priceVisible, setPriceVisible] = useState(false);
  const [howVisible, setHowVisible] = useState(false);
  const priceRef = useRef(null);
  const howSectionRef = useRef(null);
  const whyRef = useRef(null);
  const cardsRef = useRef(null);
  const howGridRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const isSwipingRef = useRef(false);
  const flipTimeoutRef = useRef(null);

  const n = cards.length;
  const windowStart = (startIndex - 1 + n) % n;
  const windowCards = Array.from(
    { length: perView + 2 },
    (_, i) => cards[(windowStart + i) % n]
  );

  useEffect(() => {
    const updatePerView = () => {
      const next = getPerView(window.innerWidth);
      setPerView((prev) => (prev === next ? prev : next));
    };
    if (typeof window !== "undefined") {
      updatePerView();
      window.addEventListener("resize", updatePerView);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updatePerView);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateColumns = () => setHowColumns(getHowColumns(window.innerWidth));
    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const measurePath = () => {
      const grid = howGridRef.current;
      if (!grid) return;
      const rect = grid.getBoundingClientRect();
      const cards = Array.from(grid.querySelectorAll(".how_card"));

      const points = cards.map((card) => {
        const cardRect = card.getBoundingClientRect();
        return {
          x: cardRect.left - rect.left + cardRect.width / 2,
          y: cardRect.top - rect.top + cardRect.height / 2,
          number: Number(card.dataset.step || 0),
        };
      });

      const pointMap = points.reduce((acc, p) => {
        if (p.number) acc[p.number] = p;
        return acc;
      }, {});

      const group1 = [1, 2, 3].map((n) => pointMap[n]).filter(Boolean);
      const group2 = [4, 5, 6].map((n) => pointMap[n]).filter(Boolean);

      setHowPath({ groups: [group1, group2], width: rect.width, height: rect.height });
    };

    const handleResize = () => window.requestAnimationFrame(measurePath);

    window.requestAnimationFrame(measurePath);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [howCards, howColumns]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setIsTouchDevice(isTouchEnvironment());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    if (!isTouchDevice && flippedCardIndex !== null) {
      setFlippedCardIndex(null);
      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
        flipTimeoutRef.current = null;
      }
    }
  }, [isTouchDevice, flippedCardIndex]);

  useEffect(() => () => {
    if (flipTimeoutRef.current) {
      clearTimeout(flipTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    setStartIndex(0);
    setNoTransition(true);
    setOffset(-step);
    const raf = requestAnimationFrame(() => setNoTransition(false));
    return () => cancelAnimationFrame(raf);
  }, [perView, step]);

  const toggleFAQ = (i) => setActiveQuestion(activeQuestion === i ? null : i);

  const handleNext = () => {
    if (animating) return;
    setAnimating(true);
    setDirection("next");
    setNoTransition(false);
    cardsRef.current?.classList.add("animating");
    setOffset(-2 * step);
  };

  const handlePrev = () => {
    if (animating) return;
    setAnimating(true);
    setDirection("prev");
    setNoTransition(false);
    cardsRef.current?.classList.add("animating");
    setOffset(0);
  };

  const handleTouchStart = (event) => {
    if (!isTouchDevice) return;
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    isSwipingRef.current = false;
  };

  const handleTouchMove = (event) => {
    if (!isTouchDevice) return;
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    if (!isSwipingRef.current) {
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        isSwipingRef.current = true;
      } else if (Math.abs(deltaY) > Math.abs(deltaX)) {
        touchStartXRef.current = null;
        touchStartYRef.current = null;
        return;
      }
    }

    if (isSwipingRef.current) {
      event.preventDefault();
    }
  };

  const handleTouchEnd = (event) => {
    if (!isTouchDevice) return;
    if (touchStartXRef.current === null) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const swipeThreshold = 50;

    if (Math.abs(deltaX) > swipeThreshold) {
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
    isSwipingRef.current = false;
  };

  const handleCardTap = (cardIndex, event) => {
    if (!isTouchDevice) return;
    if (event?.target?.closest("button")) return;

    setFlippedCardIndex((prev) => {
      const next = prev === cardIndex ? null : cardIndex;
      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
        flipTimeoutRef.current = null;
      }
      if (next !== null) {
        flipTimeoutRef.current = setTimeout(() => {
          setFlippedCardIndex(null);
          flipTimeoutRef.current = null;
        }, 2000);
      }
      return next;
    });
  };

  const handleTransitionEnd = () => {
    if (!animating) return;
    setNoTransition(true);
    setStartIndex((prev) =>
      direction === "next" ? (prev + 1) % n : (prev - 1 + n) % n
    );
    requestAnimationFrame(() => {
      setOffset(-step);
      requestAnimationFrame(() => {
        setNoTransition(false);
        setAnimating(false);
        setDirection(null);
        cardsRef.current?.classList.remove("animating");
      });
    });
  };


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setPriceVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );
    const target = priceRef.current;
    if (target instanceof Element) observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const target = howSectionRef.current;
    if (!(target instanceof Element)) return;
    const observer = new IntersectionObserver(([entry]) => setHowVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setNoTransition(false), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const target = whyRef.current;
    if (!(target instanceof Element)) return;
    const tags = target.querySelectorAll(".tag");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tags.forEach((tag, index) => {
              tag.style.animationDelay = `${index * 0.1}s`;
              tag.classList.add("animate-in");
              tag.classList.remove("animate-out");
            });
          } else {
            tags.forEach((tag, index) => {
              tag.style.animationDelay = `${index * 0.05}s`;
              tag.classList.remove("animate-in");
              tag.classList.add("animate-out");
            });
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing">
      <Header />

      {/* Hero */}
      <section className="hero">
        <div className="hero-text">
          <h1>
            {heroHeadingLine1}
            <br />
            {heroHeadingLine2}
            <br />
            {heroHeadingLine3}
          </h1>
          <p>{heroDescription}</p>
          <button className="btn" onClick={() => navigate("/booking")}>{heroCta}</button>
        </div>
        <div className="hero-img">
          <img src="/assets/pos_main.jpg" alt={t("home.hero.imageAlt", "SUP rider")} />
        </div>
      </section>

      {/* Price / Slider */}
      <section className={`price ${priceVisible ? "visible" : "hidden"}`} id="price" ref={priceRef}>
        <h2>{priceTitle}</h2>
        <div className="slider-container">
          <button className="arrow left" type="button" onClick={handlePrev}><FaChevronLeft /></button>
          <div className="cards-wrapper">
            <div
              className={`cards ${noTransition ? "no-transition" : ""}`}
              ref={cardsRef}
              style={{ "--per": perView, transform: `translateX(${offset}%)` }}
              onTransitionEnd={handleTransitionEnd}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {windowCards.map((card, index) => {
                const cardIndex = (windowStart + index) % n;
                return (
                  <div
                    className={`card ${card.type} ${flippedCardIndex === cardIndex ? "is-flipped" : ""
                      }`}
                    key={`${card.key}-${cardIndex}`}
                    onClick={(event) => handleCardTap(cardIndex, event)}
                  >
                    <div className="flip-card-container">
                      <div className="flip-card">
                        <div className="card-front">
                          <h3>{card.title}</h3>
                          <p className="price">
                            {getPriceLabel(card, pricingRowsByKey, pricingRowsById)}
                          </p>
                          <ul>
                            {card.items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                          {card.img && (
                            <img src={card.img} alt={card.title} className="card-img" />
                          )}
                        </div>
                        <div className="card-back">
                          <button onClick={() => navigate("/booking")}>{t("home.price.cardCta", "Book")}</button>
                          <div className="design-container">
                            {[...Array(8)].map((_, i) => (
                              <span key={i} className={`design design--${i + 1}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <button className="arrow right" type="button" onClick={handleNext}><FaChevronRight /></button>
        </div>
      </section>


      {/* How it works */}
      <section
        className={`how_container ${howVisible ? "is-visible" : "is-hidden"}`}
        ref={howSectionRef}
      >
        <h2>{howTitle}</h2>
        <div className="how_grid" ref={howGridRef}>
          {howPath.groups?.length && howPath.width && howPath.height ? (
            <svg
              className="how_path"
              viewBox={`0 0 ${howPath.width} ${howPath.height}`}
              preserveAspectRatio="none"
            >
              {howPath.groups.map((groupPoints, idx) => {
                const d = buildWavyPath(groupPoints, 32);
                if (!d) return null;
                return <path key={idx} d={d} />;
              })}
            </svg>
          ) : null}
          {orderedHowCards.map((card, index) => {
            const Icon = card.icon;
            const columns = Math.max(1, howColumns);
            const row = Math.floor(index / columns);
            const col = index % columns;
            const displayCol = row % 2 === 0 ? col + 1 : columns - col;
            const layoutStyle =
              howColumns >= 3
                ? { gridColumn: displayCol, gridRow: row + 1 }
                : {};

            return (
              <div
                className="how_card"
                key={index}
                style={layoutStyle}
                data-step={card.number}
              >
                <div className="how_card-badge">{card.number}</div>
                <Icon className="how_card-icon" />
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Us */}
      <section className="why" ref={whyRef}>
        <h2>{whyTitle}</h2>
        <div className="tags">
          {whyTags.map((tag, index) => (
            <span key={index} className={`tag ${tag.variant}`}>{tag.text}</span>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section" id="faq">
        <div className="faq-header">
          <h2 className={` ${faqVisible ? "open" : ""}`}
            onClick={() => setFaqVisible(!faqVisible)}
            aria-label={faqToggleLabel}>{faqTitle}</h2>
          <button
            className={`faq-toggle ${faqVisible ? "open" : ""}`}
            onClick={() => setFaqVisible(!faqVisible)}
            aria-label={faqToggleLabel}
          >
            <FaChevronDown />
          </button>
        </div>

        <div className={`faq-list ${faqVisible ? "visible" : "hidden"}`}>
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`faq-item ${activeQuestion === i ? "active" : ""}`}
              onClick={() => toggleFAQ(i)}
            >
              <div className="faq-question">
                <span>{faq.question}</span>
                {activeQuestion === i ? <FaChevronUp /> : <FaChevronDown />}
              </div>
              {activeQuestion === i && (
                <div className="faq-answer"><p>{faq.answer}</p></div>
              )}
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default HomePage;
